/**
 * usePackConfig - Fetches pack pricing from API, falls back to static
 *
 * Used by checkout and MuxVideoPlayerWithCTA. From first render, getPackPricing
 * uses static PACKS so checkout never blocks on the API (sales continue if API/DB is down).
 * When the API responds, pricing updates for display; create-payment-intent uses DB server-side.
 */

import { useEffect, useState, useCallback } from "react";
import { getPackPricing, PACKS, type PackPricing } from "@/config/stripe";

const CONFIG_PACKS_URL = "/api/config-packs";
const FETCH_TIMEOUT_MS = 2500;

/** Validate pack has required fields for checkout */
function isValidPackForCheckout(p: Record<string, unknown>): boolean {
  if (!p?.id || typeof p.id !== "string") return false;
  if (!p?.name || typeof p.name !== "string") return false;
  if (!p?.nameEs || typeof p.nameEs !== "string") return false;
  const amount = typeof p.amount === "number" ? p.amount : parseInt(String(p.amount), 10);
  if (!Number.isInteger(amount) || amount <= 0) return false;
  if (!p?.currency || typeof p.currency !== "string") return false;
  return true;
}

function buildPacksMap(packs: Array<{
  id: string;
  name: string;
  nameEs: string;
  description: string;
  descriptionEs: string;
  amount: number;
  currency: string;
  thumbnail?: string;
  fallbackPlaybackId?: string;
  priceByCountry?: Record<string, { 
    amount: number; 
    currency: string;
    playbackId?: string;
    ctaUrl?: string;
    ctaLabelEs?: string;
    ctaLabelEn?: string;
    isActive?: boolean;
    updatedAt?: string;
  }>;
}>): Record<string, PackPricing> {
  const map: Record<string, PackPricing> = {};
  for (const p of packs) {
    map[p.id] = {
      id: p.id,
      name: p.name,
      nameEs: p.nameEs,
      description: p.description,
      descriptionEs: p.descriptionEs,
      amount: p.amount,
      currency: p.currency,
      thumbnail: p.thumbnail,
      priceByCountry: p.priceByCountry,
    };
  }
  return map;
}

function getPricingFromMap(
  packs: Record<string, PackPricing>,
  packId: string,
  countryCode?: string
): PackPricing | null {
  const pack = packs[packId];
  if (!pack) return null;
  if (!countryCode || !pack.priceByCountry) return pack;
  const cp = pack.priceByCountry[countryCode];
  if (cp) return { ...pack, amount: cp.amount, currency: cp.currency };
  return pack;
}

export function usePackConfig() {
  const [packsFromApi, setPacksFromApi] = useState<Record<string, PackPricing> | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      setIsReady(true);
    }, FETCH_TIMEOUT_MS);

    fetch(CONFIG_PACKS_URL, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        // API now returns { packs: { channel_invite: {...} } } (object, not array)
        if (data?.packs && typeof data.packs === "object") {
          // Convert object to array for validation
          const packsArray = Object.values(data.packs);
          const valid = packsArray.filter((p: Record<string, unknown>) => isValidPackForCheckout(p));
          if (valid.length > 0) {
            setPacksFromApi(buildPacksMap(valid));
          }
        }
        // Legacy support: if packs is an array
        else if (data?.packs && Array.isArray(data.packs)) {
          const valid = data.packs.filter((p: Record<string, unknown>) => isValidPackForCheckout(p));
          if (valid.length > 0) {
            setPacksFromApi(buildPacksMap(valid));
          }
        }
      })
      .catch(() => {
        /* use static fallback */
      })
      .finally(() => {
        clearTimeout(timeout);
        setIsReady(true);
      });

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  const packs = packsFromApi ?? PACKS;

  const getPackPricingFn = useCallback(
    (packId: string, countryCode?: string) =>
      getPricingFromMap(packs, packId, countryCode) ?? getPackPricing(packId, countryCode),
    [packs]
  );

  return {
    packs,
    isReady,
    source: packsFromApi ? "api" : "static",
    getPackPricing: getPackPricingFn,
  };
}
